import { useStaticQueryWithPreview } from "@tedgustaf/gatsby-plugin-sanity-preview";
import classNames from "classnames";
import { graphql, useStaticQuery } from "gatsby";
import React, { useContext, useEffect, useMemo } from "react";
import Swiper from "swiper";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import { Navigation, Pagination } from "swiper/modules";
import googleCustomerReviews from "../assets/images/google-customer-reviews.png";
import poweredByGoogle from "../assets/images/powered-by-google.png";
import { PageContext } from "../context/PageContext";
import useReviews from "../hooks/useReviews";
import useTranslations from "../hooks/useTranslations";
import { SiteSettings } from "../models/shared/SiteSettings";
import { siteSettingsPreviewQueryProjection } from "../preview/staticQueries/siteSettings";
import { iconsList } from "../utilities/iconsList";
import { compareLanguages } from "../utilities/routes/utilities";
import LinkWithArrow from "./LinkWithArrow";

export interface ReviewsBlockProps {
  theme?: ReviewsBlockTheme;
  title?: string;
  text?: string;
  averageScore?: number;
  allReviewsUrl?: string;
  totalReviewsCount?: string;
  reviews?: ReviewProps[];
  placeId?: string;
  threshold?: number;
  cutOff?: number;
}

export interface ReviewProps {
  name: string;
  date: string;
  imageUrl: string;
  url: string;
  text: string;
  score: number;
}

export enum ReviewsBlockTheme {
  Blue,
  White
}

export namespace ReviewsBlockTheme {
  export const getColors = (theme?: ReviewsBlockTheme) => {
    let background: string,
      primaryText: string,
      secondaryText: string,
      tertiaryText: string,
      links: string,
      pagination: string;

    switch (theme) {
      case ReviewsBlockTheme.White:
        background = "bg-slate-50";
        primaryText = "text-blue-950";
        secondaryText = "text-neutral-40";
        tertiaryText = "text-neutral-40";
        links = "text-blue-950 border-blue-950";
        pagination = "swiper-pagination-bullet !bg-black";
        break;

      default:
        background = "bg-brand-primary-40";
        primaryText = "text-brand-senary-100";
        secondaryText = "text-review-text-50";
        tertiaryText = "text-review-text-60";
        links = "text-brand-senary-100 border-brand-senary-100";
        pagination = "swiper-pagination-bullet !bg-white";
        break;
    }

    return {
      background,
      primaryText,
      secondaryText,
      tertiaryText,
      links,
      pagination
    };
  };
}
export type SiteSettingsQuery = {
  allSanitySiteSettings?: {
    nodes?: SiteSettings[];
  };
};

export const ReviewsBlock = ({
  theme,
  title: initialHeading,
  text: initialBodyText,
  averageScore,
  allReviewsUrl,
  totalReviewsCount,
  reviews,
  placeId: initialPlaceId,
  cutOff,
  threshold: initialThreshold
}: ReviewsBlockProps) => {
  const [getReviews] = useReviews();

  const siteSettingsQuery = useStaticQueryWithPreview({
    staticQueryData: useStaticQuery<SiteSettingsQuery>(graphql`
      query sanitySiteSettings {
        ...SanitySiteSettingsFragment
      }
    `),
    query: siteSettingsPreviewQueryProjection,
    options: useMemo(() => ({ addRawFields: true }), [])
  });

  const pageContext = useContext(PageContext);
  const translations = useTranslations();

  const siteSettings =
    siteSettingsQuery.allSanitySiteSettings?.nodes?.find(node =>
      compareLanguages(node.i18nLang, pageContext.i18nLang)
    ) ?? ({} as SiteSettings);

  const isDefaultBlock = !initialPlaceId;
  const placeId =
    initialPlaceId ||
    siteSettings.defaultReviewBlock?.reviewBlockOffice?.reviewPlace
      ?.googleBusinessAccountId;
  const heading =
    (isDefaultBlock ? siteSettings.defaultReviewBlock?.title : initialHeading) ?? "";
  const bodyText =
    (isDefaultBlock ? siteSettings.defaultReviewBlock?.text : initialBodyText) ?? "";

  const thresholdForAverageScore =
    (isDefaultBlock
      ? siteSettings.defaultReviewBlock?.threshold
      : initialThreshold ?? siteSettings.reviewThreshold) ?? 0;

  const showAverageScore = (averageScore ?? 0) >= thresholdForAverageScore;

  const cachedReviews = getReviews(placeId ?? "");

  cutOff = cachedReviews?.cutOff;

  reviews = cachedReviews?.reviews
    ?.sort((a, b) => b.score - a.score)
    .filter(review => review.text !== "");

  totalReviewsCount =
    cachedReviews?.totalReviewsCount + " " + translations.reviewsReviewsCount;

  allReviewsUrl = cachedReviews?.linkToAllReviews;

  averageScore = +(cachedReviews?.averageScore?.toFixed(1) ?? "0");

  if (cutOff) {
    reviews = reviews?.filter(review => review.score >= (cutOff ?? 0));
  }

  const themeColors = ReviewsBlockTheme.getColors(theme);

  const style = {
    reviewsBlock: classNames(themeColors.background, "slice-both--long-section"),
    heading: classNames(themeColors.primaryText, "text-2xl"),
    bodyText: classNames(themeColors.secondaryText, "uppercase text-base font-bold"),
    averageScore: classNames(
      themeColors.primaryText,
      "text-7xl leading-none font-bold text-center"
    ),
    totalReviewsCount: classNames(themeColors.tertiaryText),
    link: classNames(themeColors.links, "text-base"),

    reviewBox: "rounded-lg bg-white shadow-lg",
    reviewName: "text-blue-950 text-lg font-bold",
    reviewDate: "text-gray-500 uppercase text-sm",
    reviewImage: "rounded-full shadow-lg",
    reviewText: "text-blue-950 text-sm",
    reviewLink: "text-sky-600 border-sky-600 text-base"
  };

  const renderVisualScore = (
    scoreToRender?: number,
    renderSmall?: boolean
  ): React.JSX.Element[] => {
    const visualScore: React.JSX.Element[] = [];

    if (scoreToRender) {
      Array.from({ length: 5 }).forEach((_, index) => {
        const boxIsFilled = scoreToRender >= index + 1;
        const starUrl = boxIsFilled
          ? iconsList.reviewStarFilled
          : iconsList.reviewStarEmpty;
        visualScore.push(
          <svg className={renderSmall ? "w-5 h-5" : "w-8 h-8"}>
            <use xlinkHref={starUrl} />
          </svg>
        );
      });
    }

    return visualScore;
  };

  const renderReviewBox = (
    review: ReviewProps,
    widthInPercent: number
  ): React.JSX.Element => (
    <div
      className={classNames(
        "flex flex-col gap-8 justify-between p-8 pb-10",
        style.reviewBox,
        widthInPercent === 30 ? "w-[30%]" : widthInPercent === 40 ? "w-[40%]" : ""
      )}
    >
      <div className="flex flex-row gap-4 items-center">
        <img
          className={classNames("h-16 w-16", style.reviewImage)}
          src={review.imageUrl}
          alt={review.name}
        />
        <div className="flex flex-col justify-start">
          <span className={style.reviewDate}>{review.date}</span>
          <span className={classNames("pb-1", style.reviewName)}>{review.name}</span>
          <div className="flex justify-start gap-1 h-5">
            {renderVisualScore(review.score, true)}
          </div>
        </div>
      </div>

      <span className={style.reviewText}>"{truncateComment(review.text, 200)}"</span>

      <div className="flex justify-between items-center">
        {allReviewsUrl && (
          <LinkWithArrow
            linkInfo={{
              isExternalLink: true,
              url: allReviewsUrl,
              title: translations.reviewsSeeFullReviewLinkText ?? ""
            }}
            colorClasses={style.reviewLink}
          />
        )}
        <img
          className="w-16"
          src={googleCustomerReviews}
          alt={translations.googleCustomerReviewsImageAlt ?? "Google Customer Reviews"}
        />
      </div>
    </div>
  );

  const truncateComment = (comment: string, maxLength: number): string =>
    comment.length > maxLength ? comment.substring(0, maxLength) + "..." : comment;

  const renderSwiperSlides = (reviews: ReviewProps[], threePerSlide: boolean) => {
    const reviewsPerSlide = threePerSlide ? 3 : 2;
    const slides: React.JSX.Element[] = [];
    let reviewsOnPage: ReviewProps[] = [];

    for (let i = 0; i < reviews.length; i++) {
      reviewsOnPage.push(reviews[i]);
      if (reviewsOnPage.length === reviewsPerSlide || i === reviews.length - 1) {
        slides.push(
          <div
            className={`grid grid-flow-col ml-100 mr-100 lg:mr-200 lg:ml-200 xl:mr-[8rem] xl:ml-[8rem] 2xl:mr-[10rem] 2xl:ml-[10rem] 3xl:mr-[22rem] 3xl:ml-[22rem] 4xl:mr-[40rem] 4xl:ml-[40rem] ${
              threePerSlide
                ? "gap-x-[5%] grid-cols-[30%_30%_30%]"
                : "gap-x-[5%] grid-cols-[47.5%_47.5%]"
            } mb-16`}
          >
            {reviewsOnPage.map(review => renderReviewBox(review, 100))}
          </div>
        );
        reviewsOnPage = [];
      }
    }
    return (
      <div className="swiper-wrapper">
        {slides.map(slide => (
          <div className="swiper-slide">{slide}</div>
        ))}
      </div>
    );
  };

  useEffect(() => {
    const swiper = new Swiper(".swiper", {
      modules: [Navigation, Pagination],
      pagination: {
        el: ".swiper-pagination",
        bulletClass: themeColors.pagination
      },
      navigation: {
        nextEl: ".swiper-button-next",
        prevEl: ".swiper-button-prev"
      }
    });
  });

  return (
    <>
      {reviews && reviews.length > 0 && (
        <aside
          style={{ "--swiper-navigation-top-offset": "40%" } as React.CSSProperties}
          className={classNames(
            "max-w-none py-24 flex flex-col pl-8 pr-8 md:pl-0 md:pr-0",
            style.reviewsBlock
          )}
        >
          <div className="pt-4 pb-12 flex flex-col items-start gap-8 md:flex-row md:justify-between md:pr-100 md:pl-100 lg:pr-200 lg:pl-200 xl:pr-[8rem] xl:pl-[8rem] 2xl:pr-[10rem] 2xl:pl-[10rem] 3xl:pr-[22rem] 3xl:pl-[22rem] 4xl:pr-[40rem] 4xl:pl-[40rem]">
            <div className="flex flex-col gap-4">
              <h2 className={style.heading}>{heading}</h2>
              <span className={style.bodyText}>{bodyText}</span>
            </div>

            <div className="flex gap-4 justify-start">
              {showAverageScore && (
                <>
                  <div className="flex flex-col items-center">
                    <span className={style.averageScore}>{averageScore}</span>
                    <img
                      className="w-24"
                      src={poweredByGoogle}
                      alt={translations.poweredByGoogleImageAlt ?? "Powered by Google"}
                    />
                  </div>

                  <div className="w-px border-l border-brand-senary-40"></div>
                </>
              )}

              <div className="flex flex-col justify-between items-start gap-2">
                <span className={style.totalReviewsCount}>{totalReviewsCount}</span>
                {showAverageScore && (
                  <div className="flex justify-start gap-2">
                    {renderVisualScore(averageScore, false)}
                  </div>
                )}
                {allReviewsUrl && (
                  <LinkWithArrow
                    linkInfo={{
                      isExternalLink: true,
                      url: allReviewsUrl,
                      title: translations.reviewsAllReviewsLinkText ?? ""
                    }}
                    colorClasses={style.link}
                  />
                )}
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-8 md:hidden">
            {reviews.map((review, index) => index < 2 && renderReviewBox(review, 100))}
          </div>

          <div className="hidden md:block xl:hidden">
            <div className="swiper">
              {renderSwiperSlides(reviews, false)}
              <div className="swiper-pagination"></div>
              <div className="swiper-button-prev rounded-full p-[1.75rem] !text-white bg-brand-primary-50 !left-[4.5rem] md:!left-[4rem] lg:!left-40 xl:!left-[6rem] 2xl:!left-[8rem] 3xl:!left-[20rem] 4xl:!left-[38rem]"></div>
              <div className="swiper-button-next rounded-full p-[1.75rem] !text-white bg-brand-primary-50 !right-[4.5rem] md:!right-[4rem] lg:!right-40 xl:!right-[6rem] 2xl:!right-[8rem] 3xl:!right-[20rem] 4xl:!right-[38rem]"></div>
            </div>
          </div>

          <div className="hidden xl:block">
            <div className="swiper">
              {renderSwiperSlides(reviews, true)}
              <div className="swiper-pagination"></div>
              <div className="swiper-button-prev rounded-full p-[1.75rem] !text-white bg-brand-primary-50 !left-[4.5rem] md:!left-[4rem] lg:!left-40 xl:!left-[6rem] 2xl:!left-[8rem] 3xl:!left-[20rem] 4xl:!left-[38rem]"></div>
              <div className="swiper-button-next rounded-full p-[1.75rem] !text-white bg-brand-primary-50 !right-[4.5rem] md:!right-[4rem] lg:!right-40 xl:!right-[6rem] 2xl:!right-[8rem] 3xl:!right-[20rem] 4xl:!right-[38rem]"></div>
            </div>
          </div>

          <div className="pt-12 mx-auto md:hidden">
            <LinkWithArrow
              linkInfo={{
                isExternalLink: true,
                url: allReviewsUrl,
                title: translations.reviewsAllReviewsLinkText ?? ""
              }}
              colorClasses={style.link}
            />
          </div>
        </aside>
      )}
    </>
  );
};
